import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CATEGORY_META, currency, num } from "@/lib/constants";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, LineChart, Line, CartesianGrid, AreaChart, Area } from "recharts";

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-[#7a6d5a] py-12 text-center">Loading…</div>;

  const catData = stats.by_category.map(c => ({
    name: CATEGORY_META[c.category]?.label || c.category,
    count: c.count,
    avg_score: Math.round(c.avg_score || 0),
    color: CATEGORY_META[c.category]?.color || "#d4a574",
  }));

  const stateData = stats.by_state.map(s => ({ name: s.state, count: s.count, avg_score: Math.round(s.avg_score) }));

  return (
    <div className="space-y-5" data-testid="analytics-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Market Analytics</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Distress trends across the US.</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi-frame rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">Total Records</div>
          <div className="font-display text-3xl font-bold text-[#d4a574]">{num(stats.total_opportunities)}</div>
        </div>
        <div className="kpi-frame rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">High Opportunity</div>
          <div className="font-display text-3xl font-bold text-[#34d399]">{num(stats.high_opportunity_count)}</div>
        </div>
        <div className="kpi-frame rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">Portfolio Value</div>
          <div className="font-display text-3xl font-bold">{currency(stats.total_estimated_value)}</div>
        </div>
        <div className="kpi-frame rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">Categories</div>
          <div className="font-display text-3xl font-bold text-[#7c9a85]">{stats.by_category.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-3">Category Distribution</div>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={catData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={110} innerRadius={60} strokeWidth={1.5} stroke="#14100c">
                {catData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #33291f" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {catData.map((c) => (
              <div key={c.name} className="flex items-center gap-1.5 text-[11px] text-[#b8ac96]">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                <span className="truncate">{c.name}</span><span className="font-mono ml-auto">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-3">Avg AI Score by Category</div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={catData} layout="vertical" margin={{ left: 80 }}>
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#7a6d5a" }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#b8ac96" }} width={140} />
              <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #33291f" }} />
              <Bar dataKey="avg_score" radius={[0, 3, 3, 0]}>
                {catData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712] lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-3">State Activity Heat</div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stateData}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d4a574" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#d4a574" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#33291f" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#7a6d5a" }} />
              <YAxis tick={{ fontSize: 10, fill: "#7a6d5a" }} />
              <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #33291f" }} />
              <Area type="monotone" dataKey="count" stroke="#d4a574" fill="url(#grad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
